import React, { useCallback, useRef, useState } from "react";
import {
  Box,
  Grid,
  MenuItem,
  TextField,
  Select,
  FormControl,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useLocation } from "react-router-dom";
import { VenueFormData } from "../../../../types/venue";
import apiService from "../../../../service/Api";
import { showErrorToast } from "../../../../components/common/ShowToast";

import { UploadedFiles } from "../../../../types/venue";
import VenuePhotosAdd from "../VenuePhotosAdd";
import VenueGeocodeButton from "../VenueGeocodeButton";
import { RootState } from "../../../../store/Index";
import { useSelector } from "react-redux";

interface VenueSimilarityResponse {
  status: string;
  message: string;
  similar_venues: {
    id: number;
    name: string;
    address_one: string;
    name_similarity: number;
    address_similarity: number;
  }[];
}

interface VenueEssentialsProps {
  formData: VenueFormData;
  handleInputChange: (
    e:
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | { target: { name: string; value: string } },
  ) => void;
  setFormData: React.Dispatch<React.SetStateAction<VenueFormData>>;
  uploadedFiles: UploadedFiles;
  handleFilesChange: (category: keyof UploadedFiles) => (files: File[]) => void;
  // kept for API compatibility but no longer used for rendering here
  existingFileUrls?: {
    venue: string[];
    paSystem: string[];
    equipment: string[];
    floorPlan: string[];
  };
  onRemoveExistingFile?: (
    category: "venue" | "paSystem" | "equipment" | "floorPlan",
    url: string,
  ) => void;
}

const VENUE_TYPES = [
  { value: "hotel", label: "Hotel" },
  { value: "conference_center", label: "Conference Center" },
  { value: "church", label: "Church" },
  { value: "chapel", label: "Chapel" },
  { value: "hall", label: "Hall" },
  { value: " funeral_parlour", label: "Funeral Parlour" },
  { value: " private_residence", label: "Private Residence" },
  { value: "family_home", label: "Family Home" },
  { value: "crematorium", label: "Crematorium" },
  { value: "other", label: "Other" },
];

// ── helpers ────────────────────────────────────────────────────────────────

const debounce = <T extends (...args: string[]) => void>(
  func: T,
  delay: number,
) => {
  let timeout: ReturnType<typeof setTimeout>;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), delay);
  };
};

const FieldLabel = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "textcolor",
})<{ textcolor?: string }>(({ textcolor }) => ({
  fontSize: "13px",
  fontWeight: 500,
  opacity: 0.9,
  color: textcolor,
  marginBottom: "6px",
  lineHeight: 1.4,

  "& span.required": {
    color: "#ff4d4f",
    marginLeft: "2px",
  },
}));

const StyledTextField = styled(TextField, {
  shouldForwardProp: (prop) =>
    prop !== "inputbg" && prop !== "textcolor" && prop !== "placeholdercolor",
})<{ inputbg?: string; textcolor?: string; placeholdercolor?: string }>(
  ({ inputbg, textcolor, placeholdercolor }) => ({
    width: "100%",
    "& .MuiOutlinedInput-root": {
      backgroundColor: inputbg,
      borderRadius: "30px",
      "& fieldset": { border: "none" },
      "&:hover fieldset": { border: "none" },
    },
    "& .MuiOutlinedInput-input": {
      padding: "10px 14px",
      fontSize: "13px",
      color: textcolor,
      "&::placeholder": {
        color: placeholdercolor,
        opacity: 0.9,
      },
    },
    "& .MuiFormHelperText-root": {
      marginLeft: 0,
      marginTop: "2px",
      fontSize: "0.7rem",
      lineHeight: 1.2,
    },
  }),
);

const StyledSelect = styled(Select, {
  shouldForwardProp: (prop) => prop !== "selectbg",
})<{ selectbg?: string }>(({ selectbg }) => ({
  width: "100%",
  backgroundColor: selectbg,
  borderRadius: "30px",
  fontSize: "13px",
  "& .MuiOutlinedInput-notchedOutline": { border: "none" },
  "&:hover .MuiOutlinedInput-notchedOutline": { border: "none" },
  "& .MuiSelect-select": { padding: "10px 14px" },
}));

// ── component ──────────────────────────────────────────────────────────────

export default function VenueEssentials({
  formData,
  handleInputChange,
  setFormData,
  uploadedFiles,
  handleFilesChange,
}: VenueEssentialsProps) {
  const location = useLocation();
  const isEditMode = location.pathname.toLowerCase().includes("edit");
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const cities: string[] = reduxTheme.cities ?? [];
  const [venueHelperText, setVenueHelperText] = useState(
    !formData.venueName && !formData.id ? "Venue Title is required" : "",
  );

  // ── similarity check ──────────────────────────────────────────────────

  const checkVenueSimilarity = useCallback(
    async (name: string, addressOne: string) => {
      if (!name || !addressOne) return;
      try {
        const response = await apiService.post<VenueSimilarityResponse>(
          "/api/venues/venue/check-venue-similarity/",
          { name, address_one: addressOne },
        );
        if (response.data.similar_venues?.length > 0) {
          response.data.similar_venues.forEach((venue) => {
            showErrorToast(
              `A similar venue, "${venue.name}" at "${venue.address_one}", already exists. Please verify the details.`,
            );
          });
          setVenueHelperText(
            `A similar venue already exists. Please verify the details.`,
          );
        } else {
          setVenueHelperText("");
        }
      } catch (error) {
        console.error("Venue similarity check failed:", error);
        showErrorToast("Failed to check for similar venues.");
      }
    },
    [],
  );

  const debouncedCheck = useRef(debounce(checkVenueSimilarity, 1000)).current;

  // ── unified change handler ────────────────────────────────────────────

  const handleInputChangeWithCheck = (
    e:
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | { target: { name: string; value: string } },
  ) => {
    handleInputChange(e);

    const { name, value } = e.target;

    if (isEditMode) {
      if (name === "venueName") {
        setVenueHelperText(!value ? "Venue Title is required" : "");
      }
      return;
    }

    // create mode: run similarity check when either trigger field changes
    if (name === "venueName" || name === "addressOne") {
      const updated = { ...formData, [name]: value };
      debouncedCheck(updated.venueName, updated.addressOne);
    }
  };

  const color100 = reduxTheme.primary_colors?.[100];
  const color700 = reduxTheme.primary_colors?.[700];
  const color800 = reduxTheme.primary_colors?.[800];
  const color300 = reduxTheme.primary_colors?.[300];

  // ── render ────────────────────────────────────────────────────────────

  return (
    <Box>
      <Grid container spacing={3}>
        {/* ── Row 1: Venue Name | Venue Type | Venue Capacity ── */}

        <Grid size={{ xs: 12, md: 4 }}>
          <FieldLabel textcolor={color100}>
            Venue Name <span className="required">*</span>
          </FieldLabel>

          <StyledTextField
            name="venueName"
            placeholder="e.g. Arundel School Chapel"
            value={formData.venueName || ""}
            onChange={handleInputChangeWithCheck}
            inputbg={color800}
            textcolor={color700}
            placeholdercolor={color300}
            helperText={venueHelperText}
            error={Boolean(venueHelperText)}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <FieldLabel textcolor={color100}>Venue Type</FieldLabel>
          <FormControl fullWidth>
            <StyledSelect
              name="venueType"
              value={formData.venueType || ""}
              displayEmpty
              selectbg={color800}
              onChange={(e) =>
                handleInputChange({
                  target: {
                    name: "venueType",
                    value: String(e.target.value),
                  },
                })
              }
              renderValue={(selected) =>
                selected ? (
                  <span style={{ color: color700 }}>{selected as string}</span>
                ) : (
                  <span style={{ color: color300 }}>Select type...</span>
                )
              }
            >
              {VENUE_TYPES.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </StyledSelect>
          </FormControl>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <FieldLabel textcolor={color700}>Venue Capacity</FieldLabel>
          <StyledTextField
            name="capacity"
            placeholder="e.g. 250"
            value={formData.capacity || ""}
            onChange={handleInputChange}
            type="number"
            inputbg={color800}
            textcolor={color700}
            placeholdercolor={color300}
          />
        </Grid>

        {/* ── Row 2: Address One | Address Two | Suburb ── */}

        <Grid size={{ xs: 12, md: 4 }}>
          <FieldLabel textcolor={color700}>Address One</FieldLabel>
          <StyledTextField
            name="addressOne"
            placeholder="e.g address one"
            value={formData.addressOne || ""}
            onChange={handleInputChangeWithCheck}
            inputbg={color800}
            textcolor={color700}
            placeholdercolor={color300}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <FieldLabel textcolor={color700}>Address Two</FieldLabel>
          <StyledTextField
            name="addressTwo"
            placeholder="e.g address two"
            value={formData.addressTwo || ""}
            onChange={handleInputChange}
            inputbg={color800}
            textcolor={color700}
            placeholdercolor={color300}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <FieldLabel textcolor={color700}>Suburb</FieldLabel>
          <StyledTextField
            name="suburb"
            placeholder="e.g Mount Pleasant"
            value={formData.suburb || ""}
            onChange={handleInputChange}
            inputbg={color800}
            textcolor={color700}
            placeholdercolor={color300}
          />
        </Grid>

        {/* ── Row 3: City (left-aligned, ~1/3 width) ── */}

        <Grid size={{ xs: 12, md: 4 }}>
          <FieldLabel textcolor={color100}>City</FieldLabel>
          <FormControl fullWidth sx={{ maxWidth: 280 }}>
            <StyledSelect
              name="city"
              value={formData.city}
              displayEmpty
              selectbg={color800}
              onChange={(e) =>
                handleInputChange({
                  target: {
                    name: "city",
                    value: String(e.target.value),
                  },
                })
              }
              renderValue={(selected) =>
                selected ? (
                  <span style={{ color: color700 }}>{selected as string}</span>
                ) : (
                  <span style={{ color: color300 }}>Select city...</span>
                )
              }
              MenuProps={{
                PaperProps: { sx: { maxHeight: 280 } },
              }}
            >
              <MenuItem value="">
                <em>Select city</em>
              </MenuItem>
              {cities.map((city) => (
                <MenuItem key={city} value={city}>
                  {city}
                </MenuItem>
              ))}
            </StyledSelect>
          </FormControl>
        </Grid>

        {/* ── Row 3b: Coordinates ── */}

        <Grid size={{ xs: 12 }}>
          <FieldLabel textcolor={color100}>Map Coordinates</FieldLabel>
          <VenueGeocodeButton
            venueId={formData.id}
            latitude={formData.latitude}
            longitude={formData.longitude}
            addressOne={formData.addressOne}
            addressTwo={formData.addressTwo}
            suburb={formData.suburb}
            city={formData.city}
            venueName={formData.venueName}
            onConfirm={(lat, lon) =>
              setFormData((prev) => ({
                ...prev,
                latitude: lat,
                longitude: lon,
              }))
            }
          />
        </Grid>

        {/* ── Row 4: Notes ── */}

        <Grid size={{ xs: 12 }}>
          <FieldLabel textcolor={color100}>Notes</FieldLabel>
          <StyledTextField
            name="notes"
            placeholder="Any additional notes about the venue..."
            value={formData.notes || ""}
            onChange={handleInputChange}
            inputbg={color800}
            textcolor={color700}
            placeholdercolor={color300}
            multiline
            rows={3}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "16px",
              },
              "& .MuiOutlinedInput-input": {
                padding: "10px 14px",
              },
            }}
          />
        </Grid>

        {/* ── Row 5: Social Media ── */}

        <Grid size={{ xs: 12 }}>
          <FieldLabel textcolor={color100}>Social Media & Web</FieldLabel>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <FieldLabel textcolor={color700}>Website</FieldLabel>
          <StyledTextField
            name="website"
            placeholder="https://www.example.com"
            value={formData.website || ""}
            onChange={handleInputChange}
            onBlur={(e) => {
              const val = e.target.value.trim();
              if (val && !/^https?:\/\//i.test(val)) {
                handleInputChange({
                  target: { name: "website", value: `https://${val}` },
                });
              }
            }}
            inputbg={color800}
            textcolor={color700}
            placeholdercolor={color300}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <FieldLabel textcolor={color700}>Facebook</FieldLabel>
          <StyledTextField
            name="facebook"
            placeholder="https://facebook.com/page"
            value={formData.facebook || ""}
            onChange={handleInputChange}
            onBlur={(e) => {
              const val = e.target.value.trim();
              if (val && !/^https?:\/\//i.test(val)) {
                handleInputChange({
                  target: { name: "facebook", value: `https://${val}` },
                });
              }
            }}
            inputbg={color800}
            textcolor={color700}
            placeholdercolor={color300}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <FieldLabel textcolor={color700}>Instagram</FieldLabel>
          <StyledTextField
            name="instagram"
            placeholder="https://instagram.com/handle"
            value={formData.instagram || ""}
            onChange={handleInputChange}
            onBlur={(e) => {
              const val = e.target.value.trim();
              if (val && !/^https?:\/\//i.test(val)) {
                handleInputChange({
                  target: { name: "instagram", value: `https://${val}` },
                });
              }
            }}
            inputbg={color800}
            textcolor={color700}
            placeholdercolor={color300}
          />
        </Grid>
      </Grid>

      {/* ── File Uploads (new files only — existing shown in preview panel) ── */}
      <Box sx={{ mt: 3 }}>
        <VenuePhotosAdd
          uploadedFiles={uploadedFiles}
          handleFilesChange={handleFilesChange}
        />
      </Box>
    </Box>
  );
}
